import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trash2, Play, Clock, CheckCircle2, AlertTriangle, Loader2, ChevronRight, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { addToQueue, listQueue, deleteQueueItem, processQueueNow, getAdminToken } from "@/lib/api";

const STATUS_COLORS = {
  pending: { bg: "#F1F5F9", color: "#64748B", icon: Clock },
  processing: { bg: "#EFF6FF", color: "#2563EB", icon: Loader2 },
  completed: { bg: "#ECFDF5", color: "#10B981", icon: CheckCircle2 },
  error: { bg: "#FEF2F2", color: "#EF4444", icon: AlertTriangle },
};

const BlogQueuePage = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [bulkText, setBulkText] = useState("");
  const [bulkLang, setBulkLang] = useState(lang);
  const [adding, setAdding] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!getAdminToken()) navigate("/admin/login");
  }, [navigate]);

  const refresh = useCallback(() => {
    listQueue().then(setItems).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    document.title = isNl ? "Blog Queue · HorseAppCompare" : "Blog Queue · HorseAppCompare";
    refresh();
    const interval = setInterval(refresh, 15000); // poll for status updates
    return () => clearInterval(interval);
  }, [refresh, isNl]);

  const addBulk = async (e) => {
    e?.preventDefault?.();
    const prompts = bulkText
      .split("\n")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (prompts.length === 0) return;
    setAdding(true);
    try {
      for (let i = 0; i < prompts.length; i++) {
        // schedule every 5 minutes apart so they don't pile up
        const scheduled = new Date(Date.now() + i * 5 * 60 * 1000).toISOString();
        await addToQueue({ prompt: prompts[i], language: bulkLang, scheduled_for: scheduled });
      }
      setBulkText("");
      refresh();
    } finally {
      setAdding(false);
    }
  };

  const runNow = async () => {
    setProcessing(true);
    try {
      await processQueueNow();
      refresh();
    } finally {
      setProcessing(false);
    }
  };

  const removeItem = async (id) => {
    await deleteQueueItem(id);
    refresh();
  };

  const counts = {
    pending: items.filter((i) => i.status === "pending").length,
    processing: items.filter((i) => i.status === "processing").length,
    completed: items.filter((i) => i.status === "completed").length,
    error: items.filter((i) => i.status === "error").length,
  };

  return (
    <main>
      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <div className="hac-container py-12 md:py-16 max-w-5xl">
          <nav className="flex items-center gap-2 text-xs text-[#64748B] mb-6">
            <Link to="/" className="hover:text-[#2563EB]">Home</Link>
            <ChevronRight size={12} />
            <Link to="/blog" className="hover:text-[#2563EB]">Blog</Link>
            <ChevronRight size={12} />
            <span className="text-[#0F172A] font-medium">{isNl ? "Queue" : "Queue"}</span>
          </nav>
          <h1 className="font-heading text-3xl md:text-5xl text-[#0F172A] leading-tight mb-3">
            {isNl ? "Auto-publish queue" : "Auto-publish queue"}
          </h1>
          <p className="text-[#64748B] max-w-3xl">
            {isNl
              ? "Voeg onderwerpen toe en een achtergrond-worker publiceert ze automatisch (elke 60 seconden checkt hij het volgende rijpe item). Plan zoveel je wilt — perfect voor wekelijkse content-velocity."
              : "Drop in topics and a background worker auto-publishes them (it picks the next due item every 60 seconds). Queue as many as you want — perfect for weekly content velocity."}
          </p>

          <div className="grid grid-cols-4 gap-3 mt-8 max-w-2xl">
            {Object.entries(counts).map(([k, n]) => {
              const cfg = STATUS_COLORS[k];
              return (
                <div
                  key={k}
                  className="bg-white border border-[#E2E8F0] rounded-xl p-4"
                  data-testid={`queue-count-${k}`}
                >
                  <div className="text-2xl font-bold" style={{ color: cfg.color }}>{n}</div>
                  <div className="text-xs uppercase tracking-wider text-[#64748B] mt-1">{k}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="hac-container py-12 max-w-5xl">
        <form onSubmit={addBulk} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 md:p-8 mb-8">
          <h2 className="font-heading text-2xl text-[#0F172A] mb-1">
            {isNl ? "Onderwerpen toevoegen" : "Add topics"}
          </h2>
          <p className="text-sm text-[#64748B] mb-5">
            {isNl
              ? "Eén onderwerp per regel. Items worden automatisch 5 minuten uit elkaar gepland."
              : "One topic per line. Items are auto-scheduled 5 minutes apart."}
          </p>
          <textarea
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            rows={6}
            placeholder={isNl
              ? "Beste apps voor endurance-ruiters\nEquilab vs Ridely vergelijking\nHoe digitaliseer je een pensionstal"
              : "Best apps for endurance riders\nEquilab vs Ridely comparison\nHow to digitise a boarding barn"}
            className="hac-input resize-none font-mono text-sm"
            data-testid="queue-bulk-input"
            disabled={adding}
          />
          <div className="flex items-center justify-between flex-wrap gap-3 mt-4">
            <div className="flex gap-2">
              {["en", "nl"].map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setBulkLang(l)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    bulkLang === l
                      ? "bg-[#2563EB] text-white border-[#2563EB]"
                      : "bg-white border-[#E2E8F0] text-[#64748B] hover:border-[#2563EB]"
                  }`}
                  data-testid={`queue-lang-${l}`}
                >
                  {l === "en" ? "🇬🇧 EN" : "🇳🇱 NL"}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={runNow}
                disabled={processing || counts.pending === 0}
                className="hac-btn-secondary disabled:opacity-50"
                data-testid="queue-run-now"
              >
                <Play size={14} /> {processing ? "…" : isNl ? "Verwerk nu" : "Run next now"}
              </button>
              <button
                type="submit"
                disabled={adding || !bulkText.trim()}
                className="hac-btn-primary disabled:opacity-50"
                data-testid="queue-add-submit"
              >
                <Plus size={14} /> {adding ? "…" : isNl ? "Toevoegen aan queue" : "Add to queue"}
              </button>
            </div>
          </div>
        </form>

        <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
            <h3 className="font-bold text-[#0F172A]">{isNl ? "In de queue" : "In the queue"}</h3>
            <span className="text-xs text-[#94A3B8] font-mono">{items.length} items · auto-refresh 15s</span>
          </div>
          {items.length === 0 ? (
            <div className="text-center py-16 text-[#94A3B8]">
              {isNl ? "Nog niets in de queue." : "Nothing in the queue yet."}
            </div>
          ) : (
            <ul className="divide-y divide-[#E2E8F0]">
              {items.map((it) => {
                const cfg = STATUS_COLORS[it.status] || STATUS_COLORS.pending;
                const Icon = cfg.icon;
                const due = new Date(it.scheduled_for);
                return (
                  <li key={it.id} className="px-6 py-4 flex items-center gap-4" data-testid={`queue-item-${it.id}`}>
                    <span
                      className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: cfg.bg, color: cfg.color }}
                    >
                      <Icon size={16} className={it.status === "processing" ? "animate-spin" : ""} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#0F172A] font-medium line-clamp-1">{it.prompt}</div>
                      <div className="text-xs text-[#94A3B8] mt-0.5">
                        {it.language.toUpperCase()} · {due.toLocaleString(isNl ? "nl-NL" : "en-US")}
                        {it.error && <span className="text-[#EF4444] ml-2">· {it.error}</span>}
                      </div>
                    </div>
                    {it.article_slug && (
                      <Link
                        to={`/blog/${it.article_slug}`}
                        className="text-xs text-[#2563EB] hover:underline inline-flex items-center gap-1"
                      >
                        {isNl ? "Bekijk" : "View"} <ArrowRight size={12} />
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={() => removeItem(it.id)}
                      className="text-[#94A3B8] hover:text-[#EF4444] p-1"
                      data-testid={`queue-delete-${it.id}`}
                      aria-label="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="mt-8 text-xs text-[#94A3B8] text-center">
          {isNl
            ? "De achtergrond-worker tikt elke 60 seconden. Items met scheduled_for ≤ nu worden in volgorde verwerkt."
            : "The background worker ticks every 60 seconds. Items where scheduled_for ≤ now are processed in order."}
        </div>
      </section>
    </main>
  );
};

export default BlogQueuePage;
